/** tm/components/ReportSection.tsx — 상담 결과 리포트 섹션 (결과입력 모달 안)
 *  상태·경고·미리보기·다운로드를 한 자리에서 보여준다.
 *  메모를 올리면 ReportMemoBar 가 초안을 실어 보내고, 여기서 곧바로 발행한다(자동).
 *  검수(좌우 비교·되돌리기·다시 다듬기)는 3-B 의 ReportReviewPanel 로 연결한다.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { C, T, R } from './ui';
import * as rapi from '../api-report';
import type { ReportState, ReportTodo } from '../api-report';
import { DRAFT_READY } from './ReportMemoBar';
import type { DraftReadyDetail } from './ReportMemoBar';
import ReportSendModal from './ReportSendModal';

interface Props {
  consultId: string;
  saved: boolean;                  // 결과가 한 번이라도 저장됐는지 (안내 문구용)
  onToast: (msg: string, type?: string) => void;
  /** 03 해야할 일 — 상위(결과입력 모달)가 들고 있는 행을 그대로 받아 생성에 넘긴다 */
  todos: ReportTodo[];
  /** 화면의 01·02 원문 — 저장 전이라도 리포트에 그대로 반영되도록 함께 보낸다 */
  contentCompany: string;
  contentConsult: string;
  /** 주면 미리보기를 이 컴포넌트가 그리지 않고 상위(모달 좌측 패널)에 넘긴다 */
  onPreview?: (url: string) => void;
}

const POLL_MS = 2500;
const MAX_POLL = 40;               // 최대 100초

// 색은 전부 ui.tsx 의 디자인 토큰(C)만 쓴다 — 화면 톤앤매너 통일
const STATUS_LABEL: Record<string, { text: string; color: string; bg: string }> = {
  none: { text: '미생성', color: C.muted, bg: C.bg },
  generating: { text: '생성 중', color: C.accent, bg: C.accentLt },
  ready: { text: '발행됨', color: C.green, bg: C.greenLt },
  sent: { text: '발송됨', color: C.green, bg: C.greenLt },
  stale: { text: '재생성 필요', color: C.orange, bg: C.orangeLt },
  failed: { text: '실패', color: C.red, bg: C.redLt },
};

export default function ReportSection({ consultId, saved, onToast, todos, contentCompany, contentConsult, onPreview }: Props) {
  const [state, setState] = useState<ReportState | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCount = useRef(0);
  const wasSaved = useRef(saved);

  const load = useCallback(async () => {
    try {
      const next = await rapi.getReport(consultId);
      setState(next);
    } catch { /* 미저장 상담이면 조용히 넘어감 */ }
  }, [consultId]);

  useEffect(() => { load(); }, [load]);

  // 생성 실행 — 넘겨받은 내용이 있으면 그것으로, 없으면 화면의 현재 값으로
  const run = useCallback(async (payload?: { todos: ReportTodo[]; content_company: string; content_consult: string }) => {
    setBusy(true);
    try {
      await rapi.generateReport(consultId, payload || {
        todos, content_company: contentCompany, content_consult: contentConsult,
      });
      onToast('리포트를 만들고 있습니다');
      setState(prev => (prev ? { ...prev, report_status: 'generating' } : prev));
      pollCount.current = 0;
      setTimeout(load, 1200);
    } catch (e) { onToast(e instanceof Error ? e.message : '생성 실패', 'error'); }
    finally { setBusy(false); }
  }, [consultId, todos, contentCompany, contentConsult, onToast, load]);

  // 메모에서 01·02·03 을 막 채웠으면 그 초안으로 바로 발행한다.
  // 상위 상태 갱신을 기다리지 않고 이벤트에 실려온 값을 쓰기 때문에 화면과 리포트가 어긋나지 않는다.
  useEffect(() => {
    const onReady = (e: Event) => {
      const d = (e as CustomEvent<DraftReadyDetail>).detail;
      if (!d || d.consultId !== consultId) return;
      run({ todos: d.todos || [], content_company: d.company || '', content_consult: d.consult || '' });
    };
    window.addEventListener(DRAFT_READY, onReady);
    return () => window.removeEventListener(DRAFT_READY, onReady);
  }, [consultId, run]);

  // 저장을 막 마쳤고 리포트가 발행돼 있으면 발송을 물어본다 (2026-08-17 사용자 요청)
  // 이미 저장된 상담을 열었을 때는 뜨지 않는다 — false -> true 로 바뀐 순간에만.
  useEffect(() => {
    const st = state?.report_status;
    if (!wasSaved.current && saved && (st === 'ready' || st === 'stale')) setSendOpen(true);
    wasSaved.current = saved;
  }, [saved, state?.report_status]);

  // 발행되면 미리보기를 바로 띄운다 (버전이 바뀌면 새로 받는다)
  useEffect(() => {
    const st = state?.report_status;
    if (st !== 'ready' && st !== 'sent' && st !== 'stale') { setPreviewUrl(''); return; }
    let alive = true;
    rapi.getPreviewUrl(consultId)
      .then(r => { if (alive) setPreviewUrl(r.url); })
      .catch(() => { if (alive) setPreviewUrl(''); });
    return () => { alive = false; };
  }, [consultId, state?.report_status, state?.report_version]);

  // 미리보기를 상위로 올린다 (모달이 넓어지며 좌측 패널에 그린다)
  useEffect(() => { if (onPreview) onPreview(previewUrl); }, [previewUrl, onPreview]);

  // 생성 중이면 폴링 — 완료되면 자동으로 멈춘다
  useEffect(() => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    if (state?.report_status !== 'generating') { pollCount.current = 0; return; }
    if (pollCount.current >= MAX_POLL) { onToast('리포트 생성이 지연되고 있습니다', 'error'); return; }
    pollRef.current = setTimeout(() => { pollCount.current += 1; load(); }, POLL_MS);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [state, load, onToast]);

  async function handleDownload() {
    try {
      const r = await rapi.getDownloadUrl(consultId);
      window.open(r.url, '_blank');
    } catch (e) { onToast(e instanceof Error ? e.message : '다운로드 실패', 'error'); }
  }

  const btn = (primary?: boolean) => ({
    padding: '7px 14px', borderRadius: R.sm, fontSize: T.xs, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${C.accent}`, background: primary ? C.accent : C.white, color: primary ? '#fff' : C.accent,
  });

  const st = state?.report_status || 'none';
  const badge = STATUS_LABEL[st] || STATUS_LABEL.none;
  const warnings = (state?.report?._warnings as string[]) || [];
  const review = state?.report?._review;
  const changedCount = review
    ? [...(review.company || []), ...(review.consult || [])].filter(r => r.changed).length
    : 0;

  return (
    <div>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: R.sm, padding: 12, background: C.white }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: T.xs, fontWeight: 700 }}>발행 상태</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: badge.color, background: badge.bg, padding: '2px 8px', borderRadius: 10 }}>{badge.text}</span>
          {state?.report_version ? <span style={{ fontSize: 10, color: C.muted }}>v{state.report_version}</span> : null}
        </div>

        {/* 상태별 본문 */}
        {st === 'none' && (
          <div style={{ padding: '10px 0', fontSize: T.xs, color: C.muted, lineHeight: 1.7 }}>
            맨 위에 상담 메모를 올리면 01·02·03이 채워지고 리포트가 자동으로 만들어집니다.
            <br />메모 없이 직접 적으셨다면 아래 [리포트 만들기]를 눌러주세요.
          </div>
        )}

        {st === 'generating' && (
          <div style={{ padding: '14px 0', fontSize: T.xs, color: C.accent, textAlign: 'center' }}>
            리포트를 만들고 있습니다… (문장 다듬기 · PDF 변환에 10~20초 걸립니다)
          </div>
        )}

        {st === 'failed' && (
          <div style={{ padding: '12px 0' }}>
            <div style={{ fontSize: T.xs, color: C.red, marginBottom: 8 }}>생성 실패 — {state?.report_error}</div>
            <button onClick={() => run()} disabled={busy} style={btn(true)}>다시 시도</button>
          </div>
        )}

        {st === 'stale' && (
          <div style={{ padding: '10px 0', fontSize: T.xs, color: C.orange }}>
            상담 내용이 바뀌었습니다. 아래에서 다시 만들어 주세요.
          </div>
        )}

        {(st === 'ready' || st === 'sent' || st === 'stale') && state?.report && (
          <div style={{ paddingTop: 10 }}>
            {changedCount > 0 && (
              <div style={{ fontSize: T.xs, color: C.accent, background: C.accentLt, padding: '7px 10px', borderRadius: R.sm, marginBottom: 8 }}>
                ✏️ {changedCount}개 문장이 다듬어졌습니다 — 검수에서 확인하세요
              </div>
            )}
            {warnings.length > 0 && (
              <div style={{ fontSize: T.xs, color: C.orange, background: C.orangeLt, padding: '7px 10px', borderRadius: R.sm, marginBottom: 8, lineHeight: 1.7 }}>
                {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
            <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 10 }}>
              {(state.report._pages as number) || 1}페이지
              {state.report_generated_at ? ` · ${String(state.report_generated_at).slice(0, 16).replace('T', ' ')}` : ''}
              {state.download_code ? ` · 링크 /r/${state.download_code}` : ''}
            </div>
          </div>
        )}

        {/* 미리보기 — 상위에서 그리면(넓은 모달 좌측) 여기서는 생략 */}
        {previewUrl && !onPreview && (
          <div style={{ margin: '4px 0 10px', border: `1px solid ${C.border}`, borderRadius: R.sm, overflow: 'hidden', background: C.bg }}>
            <iframe title="상담 요약 리포트 미리보기" src={previewUrl + '#toolbar=0&navpanes=0&view=FitH'}
              style={{ width: '100%', height: 560, border: 'none', display: 'block' }} />
          </div>
        )}

        {/* 버튼 줄 */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 4 }}>
          <button onClick={() => run()} disabled={busy || st === 'generating'} style={btn(st === 'none' || st === 'stale')}>
            {st === 'none' ? '📄 리포트 만들기' : '🔄 다시 만들기'}
          </button>
          {(st === 'ready' || st === 'sent' || st === 'stale') && (
            <button onClick={handleDownload} style={btn()}>📥 PDF 다운로드</button>
          )}
          {(st === 'ready' || st === 'sent' || st === 'stale') && (
            <button onClick={() => setSendOpen(true)} style={btn(st !== 'sent')}>
              {st === 'sent' ? '📤 다시 보내기' : '📤 FP·WM에게 보내기'}
            </button>
          )}
        </div>

        {sendOpen && (
          <ReportSendModal consultId={consultId} onToast={onToast}
            onClose={() => setSendOpen(false)} onSent={load} />
        )}

        {!saved && (
          <div style={{ fontSize: 10, color: C.muted, marginTop: 8, lineHeight: 1.6 }}>
            아직 저장 전입니다 — 리포트는 화면에 보이는 01·02·03 그대로 만들어집니다.
            내용을 확정하셨으면 아래 [저장]도 눌러주세요.
          </div>
        )}
      </div>
    </div>
  );
}
